/**
 * URL routing, hand-rolled on the History API.
 *
 * M1 shipped no router on purpose (three views, a discriminated union). That
 * stopped holding the moment magic links started arriving as URLs: a sign-in
 * link IS a route, and a reload must not dump the reader back at the start.
 *
 * Why not react-router: this application has eight routes, no nested layouts,
 * no loaders, no data APIs. The whole matcher below is ~40 lines and has no
 * dependency; the same reasoning that kept i18next and a query library out.
 * The seam is `ROUTES` + `useRouteMatch()` — swapping in a library later means
 * reimplementing those two, nothing else knows how navigation works.
 *
 * Paths are German because the product is German (`/anmelden`, `/einladung`,
 * `/lauf/…`), and because the backend already mails `/anmelden?token=…` and
 * `/einladung?token=…` — those two are a contract with app/auth.py, not a
 * cosmetic choice.
 */
import {
  useSyncExternalStore,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react'

export const ROUTES = {
  home: '/',
  /** Contract with app/auth.py: the mailed sign-in link points here. */
  signIn: '/anmelden',
  /** Contract with app/auth.py: the mailed invite link points here. */
  invite: '/einladung',
  workspaceIndex: '/workspace',
  workspace: '/workspace/:workspaceId',
  upload: '/workspace/:workspaceId/dossier-hochladen',
  /** The facts review surface. Belongs to the dossier, not to a single run —
   *  a correction outlives the run that produced the value. */
  facts: '/workspace/:workspaceId/dossier/:dossierId/fakten',
  /**
   * The audit trail of one dossier (M5): uploads, runs, fact edits, exports —
   * who and when. Workspace-scoped like the dossier itself; the Büro that owns
   * the dossier is the party that has to answer "who changed this?".
   */
  dossierAudit: '/workspace/:workspaceId/dossier/:dossierId/verlauf',
  /** The founder dashboard (M5). Founder-only, server-side and in the UI. */
  admin: '/verwaltung',
  /**
   * The rulebase manager (M4). Workspace-scoped because a draft pack is
   * workspace-owned data — two prospect Büros can each ingest the same BZO,
   * and neither may read the other's.
   */
  rulebase: '/workspace/:workspaceId/regelwerk',
  rulebaseIngest: '/workspace/:workspaceId/regelwerk/bzo-hochladen',
  rulebaseIngestion: '/workspace/:workspaceId/regelwerk/ingestion/:ingestionId',
  /** `zh/egg` is two segments, not one encoded one — see features/rulebase. */
  rulebasePack: '/workspace/:workspaceId/regelwerk/:kanton/:gemeinde',
  run: '/lauf/:runId',
  report: '/lauf/:runId/bericht',
  /**
   * The print form of the report — A4, DE, no chrome. Contract with the PDF
   * export: headless Chromium loads this path (or injects
   * `window.__BAUPILOT_PRINT__`, see features/report/print.ts) and prints it.
   */
  reportPrint: '/lauf/:runId/bericht/druck',
} as const

export type RouteName = keyof typeof ROUTES

/**
 * Match order — most specific first. Explicit rather than sorted by segment
 * count, so adding a route forces a decision about where it belongs.
 */
const MATCH_ORDER: RouteName[] = [
  'home',
  'signIn',
  'invite',
  'admin',
  'facts',
  'dossierAudit',
  'upload',
  // Before `rulebasePack`, whose two trailing segments would otherwise swallow
  // `/regelwerk/ingestion/:id` as a Gemeinde named "ingestion" in kanton "…".
  'rulebaseIngest',
  'rulebaseIngestion',
  'rulebasePack',
  'rulebase',
  'workspace',
  'workspaceIndex',
  'reportPrint',
  'report',
  'run',
]

/** The query parameter that carries "where the reader was" across a sign-in. */
export const RETURN_PARAM = 'weiter'

export interface RouteMatch {
  name: RouteName | 'notFound'
  params: Readonly<Record<string, string>>
  pathname: string
  search: URLSearchParams
  /** pathname + search, i.e. what a redirect would have to restore. */
  href: string
}

function segments(path: string): string[] {
  return path.split('/').filter(Boolean)
}

export function matchPattern(pattern: string, pathname: string): Record<string, string> | null {
  const want = segments(pattern)
  const have = segments(pathname)
  if (want.length !== have.length) return null
  const params: Record<string, string> = {}
  for (let i = 0; i < want.length; i += 1) {
    const w = want[i]
    if (w.startsWith(':')) {
      if (!have[i]) return null
      params[w.slice(1)] = decodeURIComponent(have[i])
    } else if (w !== have[i]) {
      return null
    }
  }
  return params
}

export function matchRoute(href: string): RouteMatch {
  const [rawPath, rawSearch = ''] = href.split('?')
  const pathname = rawPath || '/'
  const search = new URLSearchParams(rawSearch)
  for (const name of MATCH_ORDER) {
    const params = matchPattern(ROUTES[name], pathname)
    if (params) return { name, params, pathname, search, href }
  }
  return { name: 'notFound', params: {}, pathname, search, href }
}

/** Fill a route pattern. `buildPath('run', { runId })` → `/lauf/run-123`. */
export function buildPath(name: RouteName, params: Record<string, string> = {}): string {
  return ROUTES[name]
    .split('/')
    .map((part) =>
      part.startsWith(':') ? encodeURIComponent(params[part.slice(1)] ?? '') : part,
    )
    .join('/')
}

/**
 * Sanitise a `?weiter=` value before navigating to it. Only same-origin paths
 * survive: a value that starts a scheme or a protocol-relative URL is dropped,
 * so a crafted sign-in link cannot bounce someone off this origin.
 */
export function safeReturnPath(value: string | null): string | null {
  if (!value) return null
  if (!value.startsWith('/') || value.startsWith('//')) return null
  if (value.startsWith('/anmelden') || value.startsWith('/einladung')) return null
  return value
}

// --------------------------------------------------------------------------
// The store: one subscription for the whole app.
// --------------------------------------------------------------------------
const listeners = new Set<() => void>()

function read(): string {
  if (typeof window === 'undefined') return '/'
  return window.location.pathname + window.location.search
}

let snapshot = read()

function publish() {
  const next = read()
  if (next === snapshot) return
  snapshot = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    window.addEventListener('popstate', publish)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('popstate', publish)
    }
  }
}

export function currentHref(): string {
  return read()
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (typeof window === 'undefined') return
  if (to === read()) return
  if (options.replace) {
    window.history.replaceState(null, '', to)
  } else {
    window.history.pushState(null, '', to)
    // A pushed navigation is a new view and starts at its top. Without this the
    // reader keeps the old scroll offset — leaving a long report for the upload
    // screen dropped them halfway down a short page. Back/forward is left to the
    // browser's own scroll restoration, which is what a reader expects there.
    // `instant` rather than smooth: this is not motion, it is a page change, and
    // the design system's motion budget has no room for a full-page scroll.
    if (window.scrollY || window.scrollX) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }
  }
  publish()
}

export function useRouteMatch(): RouteMatch {
  const href = useSyncExternalStore(subscribe, read, read)
  return matchRoute(href)
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string
  replace?: boolean
  children: ReactNode
}

/**
 * A real `<a href>` — it copies, it middle-clicks, it opens in a new tab — that
 * intercepts the plain left click and pushes history instead of reloading.
 */
export function Link({ to, replace, onClick, children, ...rest }: LinkProps) {
  const handle = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    if (event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (rest.target && rest.target !== '_self') return
    event.preventDefault()
    navigate(to, { replace })
  }
  return (
    <a href={to} onClick={handle} {...rest}>
      {children}
    </a>
  )
}
